import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { orderApi, authApi, customerApi, configApi } from '../services/api'
import StatusBadge from '../components/StatusBadge'
import Loading from '../components/Loading'
import ErrorMessage from '../components/ErrorMessage'
import EmptyState from '../components/EmptyState'
import SubmitSuccess from '../components/SubmitSuccess'
import { channelMap, formatDateTime, isDeadlineUrgent, isOverdue } from '../utils/helpers'
import { compressImage, validateImageFile, validateImageSize, validatePhotoCount } from '../utils/imageCompress'
import { uploadToOss } from '../utils/ossUpload'
import type { Order } from '../types'

interface ShareToken {
  id: string
  token: string
  expiresAt: string
}

export default function Detail() {
  const { orderNo } = useParams<{ orderNo: string }>()
  const navigate = useNavigate()
  const [order, setOrder] = useState<Order | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [activeTab, setActiveTab] = useState<'progress' | 'logistics'>('progress')

  const [showShipModal, setShowShipModal] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [shipCarrier, setShipCarrier] = useState('')
  const [shipTrackingNo, setShipTrackingNo] = useState('')
  const [shipBatchName, setShipBatchName] = useState('')
  const [shipPhotos, setShipPhotos] = useState<string[]>([])
  const [shipContactName, setShipContactName] = useState('')
  const [shipContactPhone, setShipContactPhone] = useState('')
  const [shipAddress, setShipAddress] = useState('')
  const [shipRemark, setShipRemark] = useState('')
  const [shipSpecs, setShipSpecs] = useState<Array<{ type: string; material: string; quantity: number }>>([{ type: '', material: '', quantity: 1 }])
  const [shipUseAllSpecs, setShipUseAllSpecs] = useState(true)  // 默认一次性寄齐，复用订单规格
  const [carrierDropdownOpen, setCarrierDropdownOpen] = useState(false)
  const [specDropdownOpen, setSpecDropdownOpen] = useState<string | null>(null)
  const shipFileInputRef = useRef<HTMLInputElement>(null)

  const specTypeOptions = ['单路开关', '双路开关', '五孔插座', '英标插座', '万能插座', 'USB 插座', '网口插座', '五孔地插/封闭面板']
  const specMaterialOptions = ['岩板', '瓷砖', '木饰面', '木板', '大理石', '玻璃', '金属板']

  const [shareTokens, setShareTokens] = useState<ShareToken[]>([])
  const [shareLinkCopied, setShareLinkCopied] = useState('')
  const [previewImage, setPreviewImage] = useState<string | null>(null)
  const [showRecipientForm, setShowRecipientForm] = useState(false)

  const [loginPhone, setLoginPhone] = useState('')
  const [loginCode, setLoginCode] = useState('')
  const [loginName, setLoginName] = useState('')
  const [loginCountdown, setLoginCountdown] = useState(0)

  const [pendingAction, setPendingAction] = useState<null | 'selfShip' | 'createShare'>(null)

  const [showAddressModal, setShowAddressModal] = useState(false)
  const [newAddress, setNewAddress] = useState('')
  const [newContactName, setNewContactName] = useState('')
  const [newContactPhone, setNewContactPhone] = useState('')

  const carriers = ['顺丰速运', '中通快递', '圆通快递', '申通快递', '韵达快递', '京东物流', '德邦快递', '其他']

  const [thirdPartyEnabled, setThirdPartyEnabled] = useState(false)
  useEffect(() => {
    configApi.getPublic()
      .then((cfg) => setThirdPartyEnabled(cfg.thirdPartyShipping))
      .catch(() => setThirdPartyEnabled(false))
  }, [])

  // 检测是否填写了工厂地址
  const isFactoryAddress = (name: string, phone: string, address: string): boolean => {
    const factoryPatterns = [
      name.includes('浅形定制'),
      phone === '13501680386',
      address.includes('浒墅关'),
      address.includes('浒牌路3号'),
      address.includes('306室'),
    ]
    return factoryPatterns.filter(Boolean).length >= 1
  }

  useEffect(() => {
    if (!orderNo) return
    fetchOrder()
  }, [orderNo])

  const fetchOrder = async () => {
    setLoading(true)
    setError('')
    try {
      const data: any = await orderApi.getPublic(orderNo!)
      if (typeof data.specs === 'string') {
        data.specs = JSON.parse(data.specs)
      }
      if (data.batches) {
        data.batches = data.batches.map((batch: any) => ({
          ...batch,
          photos: typeof batch.photos === 'string' ? JSON.parse(batch.photos) : (batch.photos || []),
          finishedPhotos: typeof batch.finishedPhotos === 'string' ? JSON.parse(batch.finishedPhotos) : (batch.finishedPhotos || []),
          exceptionPhotos: typeof batch.exceptionPhotos === 'string' ? JSON.parse(batch.exceptionPhotos) : (batch.exceptionPhotos || []),
          defectPhotos: typeof batch.defectPhotos === 'string' ? JSON.parse(batch.defectPhotos) : (batch.defectPhotos || []),
          specs: typeof batch.specs === 'string' ? JSON.parse(batch.specs) : (batch.specs || []),
        }))
      }
      if (data.statusLogs) {
        data.statusLogs = [...data.statusLogs].reverse()
      }
      setOrder(data as Order)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '加载失败'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  const executePendingAction = async () => {
    if (pendingAction === 'selfShip') {
      setShipUseAllSpecs(true)
      // 一次性寄齐：预填订单规格
      if (order && order.specs && Array.isArray(order.specs) && order.specs.length > 0) {
        setShipSpecs(order.specs.filter((s: any) => s.type && s.material).map((s: any) => ({ type: s.type, material: s.material, quantity: s.quantity || 1 })))
      }
      setShowShipModal(true)
      setPendingAction(null)
    } else if (pendingAction === 'createShare') {
      setPendingAction(null)
      await handleCreateShare()
    }
  }

  const handleShipPhotoFiles = async (files: FileList | File[]) => {
    if (!files || files.length === 0) return

    const fileArray = Array.from(files)
    const countError = validatePhotoCount(shipPhotos.length, fileArray.length)
    if (countError) {
      setError(countError)
      return
    }

    if (!order?.id) {
      setError('订单未加载')
      return
    }

    const newPhotos: string[] = []
    for (const file of fileArray) {
      const validation = validateImageFile(file)
      if (validation) {
        setError(validation)
        continue
      }
      const sizeError = validateImageSize(file)
      if (sizeError) {
        setError(sizeError)
        continue
      }
      try {
        const compressed = await compressImage(file)
        const uploaded = await uploadToOss(
          compressed.blob,
          file.name,
          { orderId: order.id, type: 'material_photo' },
          { width: compressed.width, height: compressed.height },
        )
        newPhotos.push(uploaded.url)
        // 移动端 Canvas 内存释放缓冲，防止连续压缩耗尽 GPU 纹理内存
        await new Promise(r => setTimeout(r, 150))
      } catch (err) {
        const msg = err instanceof Error ? err.message : '上传失败'
        setError(`图片上传失败: ${msg}`)
      }
    }
    if (newPhotos.length > 0) {
      setShipPhotos((prev) => [...prev, ...newPhotos])
      setError('')
    }
  }

  const handleSelfShip = async () => {
    if (!shipCarrier || !shipTrackingNo) {
      setError('请填写完整的快递信息')
      return
    }
    if (!shipContactName.trim() || !shipContactPhone.trim() || !shipAddress.trim()) {
      setError('请完整填写您的收件信息（姓名+电话+地址）')
      return
    }
    if (shipPhotos.length === 0) {
      setError('请上传材料照片')
      return
    }
    setLoading(true)
    setError('')
    try {
      const specsToSend = shipSpecs.filter(s => s.type && s.material).map(s => ({ type: s.type.trim(), material: s.material.trim(), quantity: s.quantity }))
      await orderApi.submitBatch(orderNo!, {
        carrier: shipCarrier,
        trackingNo: shipTrackingNo,
        batchName: shipBatchName || undefined,
        photos: shipPhotos,
        contactName: shipContactName.trim(),
        contactPhone: shipContactPhone.trim(),
        address: shipAddress.trim(),
        remark: shipRemark.trim() || undefined,
        specs: specsToSend,
      })
      setShowSuccess(true)
      setShipCarrier('')
      setCarrierDropdownOpen(false)
      setSpecDropdownOpen(null)
      setShipTrackingNo('')
      setShipBatchName('')
      setShipPhotos([])
      setShipContactName('')
      setShipContactPhone('')
      setShipAddress('')
      setShipRemark('')
      setShipSpecs([{ type: '', material: '', quantity: 1 }])
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '提交失败'
      setShowShipModal(false)
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateShare = async (contactName?: string, contactPhone?: string, contactAddress?: string) => {
    setLoading(true)
    setError('')
    try {
      const data = await orderApi.createShare(orderNo!, { 
        type: 'link',
        contactName,
        contactPhone,
        contactAddress,
      }) as ShareToken
      setShareTokens(prev => [...prev, data])
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '生成失败'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmitRecipientAndShare = async () => {
    if (!shipContactName.trim() || !shipContactPhone.trim() || !shipAddress.trim()) {
      setError('请完整填写收件信息（姓名+电话+地址）后才能生成分享链接')
      return
    }
    setShowRecipientForm(false)
    await handleCreateShare(shipContactName.trim(), shipContactPhone.trim(), shipAddress.trim())
  }

  const handleModifyAddress = async () => {
    if (!newAddress.trim()) {
      setError('请输入新地址')
      return
    }
    setLoading(true)
    setError('')
    try {
      await orderApi.modifyAddress(orderNo!, {
        address: newAddress.trim(),
        contactName: newContactName.trim() || undefined,
        contactPhone: newContactPhone.trim() || undefined,
      })
      setShowAddressModal(false)
      setNewAddress('')
      setNewContactName('')
      setNewContactPhone('')
      fetchOrder()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '修改失败'
      setShowAddressModal(false)
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  const handleRevokeShare = async (tokenId: string) => {
    setLoading(true)
    try {
      await orderApi.revokeShare(orderNo!, tokenId)
      setShareTokens(prev => prev.filter(t => t.id !== tokenId))
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '撤销失败'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  const copyShareLink = (token: string) => {
    const link = `${window.location.origin}/h5/sender?token=${token}`
    navigator.clipboard.writeText(link).then(() => {
      setShareLinkCopied(token)
      setTimeout(() => setShareLinkCopied(''), 2000)
    })
  }

  const sendLoginCode = async () => {
    if (!loginPhone.trim() || !/^1[3-9]\d{9}$/.test(loginPhone.trim())) {
      setError('请输入正确的手机号')
      return
    }
    setLoading(true)
    try {
      await authApi.sendSms(loginPhone.trim())
      setError('')
      setLoginCountdown(60)
      const timer = setInterval(() => {
        setLoginCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer)
            return 0
          }
          return prev - 1
        })
      }, 1000)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '发送失败'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  const handleLogin = async () => {
    if (!loginPhone.trim() || !loginCode.trim()) {
      setError('请填写手机号和验证码')
      return
    }
    setLoading(true)
    setError('')
    try {
      const res = await authApi.verifyPhone({
        phone: loginPhone.trim(),
        code: loginCode.trim(),
        name: loginName.trim() || undefined,
      }) as { token: string; user?: { role?: string } }
      localStorage.setItem('customer_token', res.token)
      setLoginPhone('')
      setLoginCode('')
      setLoginName('')
      setError('')
      // 仅普通客户登录时绑定订单；管理/加工账号不绑定，避免污染客户信息
      if (res.user?.role === 'customer') {
        try { await customerApi.bindOrder(orderNo!); } catch { /* already bound */ }
      }
      await executePendingAction()
      fetchOrder()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '登录失败'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('customer_token')
    setPendingAction(null)
    fetchOrder()
  }

  if (loading && !order) return <Loading />
  if (error && !order) return <ErrorMessage message={error} onRetry={fetchOrder} />
  if (!order) return <EmptyState title="订单不存在" />

  if (showSuccess) {
    return <SubmitSuccess orderNo={orderNo} />
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#eeeeee' }}>
      <div className="nav-bar">
        <button onClick={() => navigate('/query')} style={{ color: '#9a9a9a', background: 'none', border: 'none', cursor: 'pointer' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 style={{ fontFamily: '"Noto Sans SC", sans-serif', fontSize: 16, fontWeight: 500, color: '#313131', letterSpacing: '0.15em' }}>
          订单详情
        </h1>
        {(localStorage.getItem('customer_token') || localStorage.getItem('admin_token')) ? (
          <button
            onClick={handleLogout}
            style={{ color: '#d9363e', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, letterSpacing: '0.15em' }}
          >
            退出登录
          </button>
        ) : (
          <div style={{ width: 36 }} />
        )}
      </div>
      {error && <ErrorMessage message={error} onRetry={() => { setError(''); fetchOrder() }} />}

      <div style={{ padding: '24px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 40 }}>
          {order.status === 'exception' && (
            <div className="card" style={{ borderColor: '#313131', borderWidth: 2 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{
                  width: 32,
                  height: 32,
                  border: '1px solid #313131',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#313131" strokeWidth="1.5">
                    <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <h3 style={{ fontSize: 15, fontWeight: 500, color: '#313131', letterSpacing: '0.15em' }}>订单异常</h3>
                  <p style={{ fontSize: 13, color: '#313131', marginTop: 4, lineHeight: 1.8, letterSpacing: '0.15em' }}>
                    {order.exceptionReason || '加工过程中出现异常，请联系客服处理'}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="card">
            <h3 style={{ fontSize: 16, fontWeight: 500, color: '#313131', letterSpacing: '0.15em' }}>订单信息</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
              <p style={{ fontSize: 13, color: '#9a9a9a', letterSpacing: '0.15em' }}>订单号：{order.orderNo}</p>
              <p style={{ fontSize: 13, color: '#9a9a9a', letterSpacing: '0.15em' }}>销售渠道：{channelMap[order.channel] || order.channel}</p>
              <p style={{ fontSize: 13, color: '#9a9a9a', letterSpacing: '0.15em' }}>创建时间：{formatDateTime(order.createdAt)}</p>
              <p style={{ fontSize: 13, color: '#9a9a9a', letterSpacing: '0.15em' }}>客户名称：{order.customerName || order.customer?.name || '-'}</p>
              <p style={{ fontSize: 13, color: '#9a9a9a', letterSpacing: '0.15em' }}>客户手机：{order.customerPhone || order.customer?.phone || '-'}</p>
              <p style={{ fontSize: 13, color: '#9a9a9a', letterSpacing: '0.15em' }}>收货地址：{order.address || '-'}</p>
              {order.processingDeadline && (
                <p style={{
                  fontSize: 13,
                  color: '#313131',
                  letterSpacing: '0.15em',
                  fontWeight: isOverdue(order.processingDeadline) || isDeadlineUrgent(order.processingDeadline) ? 500 : 400,
                }}>
                  {isOverdue(order.processingDeadline)
                    ? `已超期：截止 ${formatDateTime(order.processingDeadline)}`
                    : `加工截止：${formatDateTime(order.processingDeadline)}`}
                </p>
              )}
            </div>

            {Array.isArray(order.specs) && order.specs.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <p style={{ fontSize: 12, color: '#9a9a9a', letterSpacing: '0.15em', marginBottom: 8 }}>定制规格</p>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #dddddd' }}>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: '#9a9a9a', fontWeight: 400, letterSpacing: '0.15em' }}>类型</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: '#9a9a9a', fontWeight: 400, letterSpacing: '0.15em' }}>材质</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: '#9a9a9a', fontWeight: 400, letterSpacing: '0.15em' }}>数量</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.specs.map((spec, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '6px 8px', color: '#313131' }}>{spec.type}</td>
                        <td style={{ padding: '6px 8px', color: '#313131' }}>{spec.material}</td>
                        <td style={{ padding: '6px 8px', color: '#313131' }}>{spec.quantity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {order.status !== 'closed' && order.status !== 'shipped' && (
            <div className="card">
              <h3 style={{ fontSize: 16, fontWeight: 500, color: '#313131', marginBottom: 16, letterSpacing: '0.15em' }}>寄送材料</h3>

              {!localStorage.getItem('customer_token') && !localStorage.getItem('admin_token') && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {(order.status as string) === 'closed' ? (
                    <p style={{ fontSize: 13, color: '#9a9a9a', letterSpacing: '0.15em' }}>订单已关闭，无法寄件</p>
                  ) : (
                    <>
                      {!pendingAction && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                          <button
                            onClick={() => { setPendingAction('selfShip'); setError('') }}
                            className="btn-primary"
                          >
                            填写快递信息
                          </button>
                          {thirdPartyEnabled && (
                          <button
                            onClick={() => { setPendingAction('createShare'); setError('') }}
                            className="btn-secondary"
                          >
                            第三方寄件
                          </button>
                          )}
                        </div>
                      )}

                      {pendingAction && (
                        <>
                          <p style={{ fontSize: 13, color: '#9a9a9a', letterSpacing: '0.15em' }}>
                            请先登录后再{pendingAction === 'selfShip' ? '提交快递信息' : '生成分享链接'}
                          </p>
                          <div>
                            <label style={{ display: 'block', fontSize: 13, color: '#9a9a9a', marginBottom: 8, letterSpacing: '0.15em' }}>姓名</label>
                            <input
                              type="text"
                              value={loginName}
                              onChange={(e) => setLoginName(e.target.value)}
                              placeholder="请输入您的姓名（首次登录时填写）"
                              className="input"
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', fontSize: 13, color: '#9a9a9a', marginBottom: 8, letterSpacing: '0.15em' }}>手机号</label>
                            <input
                              type="tel"
                              value={loginPhone}
                              onChange={(e) => setLoginPhone(e.target.value)}
                              placeholder="请输入手机号"
                              className="input"
                              maxLength={11}
                            />
                          </div>
                          <div style={{ display: 'flex', gap: 12 }}>
                            <div style={{ flex: 1 }}>
                              <label style={{ display: 'block', fontSize: 13, color: '#9a9a9a', marginBottom: 8, letterSpacing: '0.15em' }}>验证码</label>
                              <input
                                type="text"
                                value={loginCode}
                                onChange={(e) => setLoginCode(e.target.value)}
                                placeholder="验证码"
                                className="input"
                                maxLength={6}
                              />
                            </div>
                            <button
                              onClick={sendLoginCode}
                              disabled={loading || loginCountdown > 0}
                              className="btn-secondary"
                              style={{ alignSelf: 'flex-end', whiteSpace: 'nowrap', fontSize: 13 }}
                            >
                              {loginCountdown > 0 ? `${loginCountdown}秒后重发` : '获取验证码'}
                            </button>
                          </div>
                          {error && (
                            <p style={{ fontSize: 13, color: '#313131', letterSpacing: '0.15em' }}>{error}</p>
                          )}
                          <button onClick={handleLogin} disabled={loading} className="btn-primary">
                            {loading ? '登录中…' : '登录'}
                          </button>
                          <button
                            onClick={() => { setPendingAction(null); setError(''); setLoginPhone(''); setLoginCode('') }}
                            style={{ fontSize: 12, color: '#9a9a9a', background: 'none', border: 'none', cursor: 'pointer', letterSpacing: '0.15em', alignSelf: 'center' }}
                          >
                            返回
                          </button>
                        </>
                      )}
                    </>
                  )}
                </div>
              )}

              {(localStorage.getItem('customer_token') || localStorage.getItem('admin_token')) && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {(order.status as string) === 'closed' ? (
                    <p style={{ fontSize: 13, color: '#9a9a9a', letterSpacing: '0.15em' }}>订单已关闭，无法寄件</p>
                  ) : (
                    <>
                      <button onClick={() => {
                        setError('')
                        setShipUseAllSpecs(true)
                        // 一次性寄齐：预填订单规格
                        if (order.specs && Array.isArray(order.specs) && order.specs.length > 0) {
                          setShipSpecs(order.specs.filter((s: any) => s.type && s.material).map((s: any) => ({ type: s.type, material: s.material, quantity: s.quantity || 1 })))
                        }
                        setShowShipModal(true)
                      }} className="btn-primary">
                        填写快递信息
                      </button>
                      {thirdPartyEnabled && (
                      <button onClick={() => {
                        setError('')
                        setShowRecipientForm(true)
                      }} disabled={loading} className="btn-secondary">
                        分享第三方寄件链接
                      </button>
                      )}
                    </>
                  )}

                  {showRecipientForm && (
                    <div style={{ padding: 12, border: '1px solid #dddddd', backgroundColor: '#fafafa', display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <p style={{ fontSize: 13, color: '#9a9a9a', letterSpacing: '0.15em' }}>请填写定制面板收件信息（加工后成品寄往哪里）</p>
                      <input type="text" value={shipContactName} onChange={(e) => setShipContactName(e.target.value)} placeholder="收件人姓名" className="input" />
                      <input type="tel" value={shipContactPhone} onChange={(e) => setShipContactPhone(e.target.value)} placeholder="收件人电话" className="input" maxLength={11} />
                      <input type="text" value={shipAddress} onChange={(e) => setShipAddress(e.target.value)} placeholder="收件地址" className="input" />
                      {isFactoryAddress(shipContactName, shipContactPhone, shipAddress) && (
                        <p style={{ padding: '8px 12px', backgroundColor: '#fff2f0', border: '1px solid #ff4d4f', borderRadius: 4, fontSize: 13, color: '#cf1322', letterSpacing: '0.15em', lineHeight: 1.6 }}>
                          ⚠️ 您填写的似乎是浅形定制工厂的收件地址，请填写<strong>您自己的收货地址</strong>
                        </p>
                      )}
                      <textarea value={shipRemark} onChange={(e) => setShipRemark(e.target.value)} placeholder="备注（选填，如需提醒说明的信息）" className="input" rows={2} style={{ resize: 'vertical' }} />
                      <div style={{ display: 'flex', gap: 12 }}>
                        <button onClick={() => { setShowRecipientForm(false); setError('') }} className="flex-1 btn-secondary">取消</button>
                        <button onClick={handleSubmitRecipientAndShare} disabled={loading} className="flex-1 btn-primary">
                          {loading ? '生成中…' : '生成分享链接'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {error && (localStorage.getItem('customer_token') || localStorage.getItem('admin_token')) && (
                <p style={{ fontSize: 13, color: '#313131', marginTop: 4, letterSpacing: '0.15em' }}>
                  {error}
                </p>
              )}

              {thirdPartyEnabled && shareTokens.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <h4 style={{ fontSize: 13, color: '#9a9a9a', marginBottom: 8, letterSpacing: '0.15em' }}>已生成的分享链接</h4>
                  {shareTokens.map(token => (
                    <div key={token.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid #dddddd' }}>
                      <span style={{ fontSize: 13, color: '#313131', letterSpacing: '0.15em', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {token.token}
                      </span>
                      <button
                        onClick={() => copyShareLink(token.token)}
                        className="btn-ghost"
                        style={{ fontSize: 11, whiteSpace: 'nowrap' }}
                      >
                        {shareLinkCopied === token.token ? '已复制' : '复制链接'}
                      </button>
                      <button
                        onClick={() => handleRevokeShare(token.id)}
                        className="btn-ghost"
                        style={{ fontSize: 11, whiteSpace: 'nowrap' }}
                      >
                        撤销
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {order.status === 'ready_to_ship' && (
            <div className="card">
              <h3 style={{ fontSize: 16, fontWeight: 500, color: '#313131', marginBottom: 16, letterSpacing: '0.15em' }}>发货准备中</h3>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{
                  width: 32, height: 32, border: '1px solid #313131',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#313131" strokeWidth="1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <p style={{ fontSize: 15, color: '#313131', letterSpacing: '0.15em', lineHeight: 1.6 }}>
                    加工已完成，正在安排发货
                  </p>
                  <p style={{ fontSize: 13, color: '#9a9a9a', marginTop: 4, letterSpacing: '0.15em', lineHeight: 1.6 }}>
                    发货后将通过服务号通知您，并在此页面显示物流信息
                  </p>
                </div>
              </div>

              <div style={{ marginTop: 16, padding: 12, border: '1px solid #dddddd', backgroundColor: '#fafafa' }}>
                <h4 style={{ fontSize: 13, color: '#9a9a9a', marginBottom: 8, letterSpacing: '0.15em' }}>确认收货地址</h4>
                <p style={{ fontSize: 15, color: '#313131', letterSpacing: '0.15em', lineHeight: 1.8 }}>
                  {order.receiver?.name} {order.receiver?.phone}
                </p>
                <p style={{ fontSize: 15, color: '#313131', letterSpacing: '0.15em', lineHeight: 1.8 }}>
                  {order.receiver?.province}{order.receiver?.city}{order.receiver?.district}{order.receiver?.address}
                </p>
              </div>
            </div>
          )}

          <div>
            <div style={{ display: 'flex', borderBottom: '1px solid #dddddd', backgroundColor: '#ffffff' }}>
              {[
                { key: 'progress' as const, label: '批次' },
                { key: 'logistics' as const, label: '记录' },
              ].map((tab) => {
                const isLoggedIn = !!localStorage.getItem('customer_token') || !!localStorage.getItem('admin_token')
                const isActive = activeTab === tab.key
                const canClick = isLoggedIn
                return (
                  <button
                    key={tab.key}
                    onClick={() => canClick && setActiveTab(tab.key)}
                    disabled={!canClick}
                    style={{
                      flex: 1,
                      padding: '14px 0',
                      fontSize: 15,
                      fontWeight: isActive ? 500 : 400,
                      textAlign: 'center',
                      color: isActive ? '#313131' : '#9a9a9a',
                      borderBottom: isActive ? '2px solid #313131' : '2px solid transparent',
                      background: 'none',
                      borderTop: 'none',
                      borderLeft: 'none',
                      borderRight: 'none',
                      cursor: canClick ? 'pointer' : 'default',
                      fontFamily: '"Noto Sans SC", "Inter", sans-serif',
                      letterSpacing: '0.15em',
                      opacity: canClick ? 1 : 0.5,
                    }}
                  >
                    {tab.label}
                  </button>
                )
              })}
            </div>

            <div className="card" style={{ borderTop: 'none' }}>
              {!localStorage.getItem('customer_token') && !localStorage.getItem('admin_token') && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: '32px 0' }}>
                  {order.status === 'material_pending' ? (
                    <p style={{ fontSize: 13, color: '#9a9a9a', letterSpacing: '0.15em' }}>
                      请先选择寄送方式并完成验证
                    </p>
                  ) : (
                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 16 }}>
                      <p style={{ fontSize: 13, color: '#9a9a9a', letterSpacing: '0.15em' }}>
                        请登录后查看详细信息
                      </p>
                      <div>
                        <label style={{ display: 'block', fontSize: 13, color: '#9a9a9a', marginBottom: 8, letterSpacing: '0.15em' }}>姓名</label>
                        <input
                          type="text"
                          value={loginName}
                          onChange={(e) => setLoginName(e.target.value)}
                          placeholder="请输入您的姓名（首次登录时填写）"
                          className="input"
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 13, color: '#9a9a9a', marginBottom: 8, letterSpacing: '0.15em' }}>手机号</label>
                        <input
                          type="tel"
                          value={loginPhone}
                          onChange={(e) => setLoginPhone(e.target.value)}
                          placeholder="请输入手机号"
                          className="input"
                          maxLength={11}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: 12 }}>
                        <div style={{ flex: 1 }}>
                          <label style={{ display: 'block', fontSize: 13, color: '#9a9a9a', marginBottom: 8, letterSpacing: '0.15em' }}>验证码</label>
                          <input
                            type="text"
                            value={loginCode}
                            onChange={(e) => setLoginCode(e.target.value)}
                            placeholder="验证码"
                            className="input"
                            maxLength={6}
                          />
                        </div>
                        <button
                          onClick={sendLoginCode}
                          disabled={loading || loginCountdown > 0}
                          className="btn-secondary"
                          style={{ alignSelf: 'flex-end', whiteSpace: 'nowrap', fontSize: 13 }}
                        >
                          {loginCountdown > 0 ? `${loginCountdown}秒后重发` : '获取验证码'}
                        </button>
                      </div>
                      {error && (
                        <p style={{ fontSize: 13, color: '#313131', letterSpacing: '0.15em' }}>{error}</p>
                      )}
                      <button onClick={handleLogin} disabled={loading} className="btn-primary">
                        {loading ? '登录中…' : '登录'}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {(localStorage.getItem('customer_token') || localStorage.getItem('admin_token')) && activeTab === 'progress' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                  {order.batches && order.batches.length > 0 ? (
                    [...order.batches].reverse().map((batch) => {
                      const batchStatusText =
                        batch.status === 'material_pending' ? '待寄材料' :
                        batch.status === 'material_shipped' ? '材料运输' :
                        batch.status === 'received' ? '材料签收' :
                        batch.status === 'processing' ? '正在加工' :
                        batch.status === 'completed' ? '加工完成' :
                        batch.status === 'ready_to_ship' ? '待发货' :
                        batch.status === 'shipped' ? '已发货' :
                        batch.status === 'exception' ? '制作异常' : batch.status
                      const batchStatusColor =
                        batch.status === 'material_pending' ? '#fa8c16' :
                        batch.status === 'material_shipped' ? '#1677ff' :
                        batch.status === 'received' ? '#1677ff' :
                        batch.status === 'processing' ? '#722ed1' :
                        batch.status === 'completed' ? '#52c41a' :
                        batch.status === 'ready_to_ship' ? '#2f54eb' :
                        batch.status === 'shipped' ? '#52c41a' :
                        batch.status === 'exception' ? '#ff4d4f' :
                        '#9a9a9a'
                      return (
                        <div key={batch.id} style={{ border: '1px solid #dddddd', padding: 16 }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                            <span style={{ fontSize: 15, fontWeight: 500, color: '#313131', letterSpacing: '0.15em' }}>
                              第 {batch.batchIndex + 1} 批寄送
                            </span>
                            <span style={{
                              fontSize: 11,
                              color: batchStatusColor,
                              border: `1px solid ${batchStatusColor}`,
                              padding: '2px 8px',
                              letterSpacing: '0.15em',
                            }}>
                              {batchStatusText}
                            </span>
                          </div>

                          {(batch.senderName || batch.senderPhone || batch.senderAddress) && (
                            <div style={{ fontSize: 13, color: '#9a9a9a', lineHeight: 1.8, letterSpacing: '0.15em', marginBottom: 10 }}>
                              <p style={{ fontSize: 11, color: '#bbb', marginBottom: 4 }}>寄件信息</p>
                              {batch.senderName && <span>{batch.senderName} </span>}
                              {batch.senderPhone && <span>{batch.senderPhone} </span>}
                              {batch.senderAddress && <p>{batch.senderAddress}</p>}
                            </div>
                          )}

                          {(batch.carrier || batch.trackingNo) && (
                            <div style={{ fontSize: 13, color: '#9a9a9a', lineHeight: 1.8, letterSpacing: '0.15em', marginBottom: 10 }}>
                              <p style={{ fontSize: 11, color: '#bbb', marginBottom: 4 }}>快递信息</p>
                              <p>快递公司：{batch.carrier || '-'}</p>
                              <p>快递单号：{batch.trackingNo || '-'}</p>
                            </div>
                          )}

                          {batch.receivedAt && (
                            <div style={{ fontSize: 13, color: '#9a9a9a', lineHeight: 1.8, letterSpacing: '0.15em', marginBottom: 10 }}>
                              <p>签收时间：{formatDateTime(batch.receivedAt)}</p>
                            </div>
                          )}

                          {Array.isArray(batch.specs) && batch.specs.filter((s: any) => s && s.type && s.material).length > 0 && (
                            <div style={{ fontSize: 13, color: '#9a9a9a', lineHeight: 1.8, letterSpacing: '0.15em', marginBottom: 10 }}>
                              <p style={{ fontSize: 11, color: '#bbb', marginBottom: 4 }}>定制规格</p>
                              {batch.specs.filter((s: any) => s && s.type && s.material).map((s: any, i: number) => (
                                <p key={i}>{s.type} {s.material} x{s.quantity}</p>
                              ))}
                            </div>
                          )}

                          {Array.isArray(batch.photos) && batch.photos.length > 0 && (
                            <div style={{ marginBottom: 10 }}>
                              <p style={{ fontSize: 11, color: '#bbb', marginBottom: 4, letterSpacing: '0.15em' }}>材料照片</p>
                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                {batch.photos.map((url: string, i: number) => (
                                  <img
                                    key={i}
                                    src={url}
                                    alt={`材料照片 ${i + 1}`}
                                    style={{ width: 80, height: 80, objectFit: 'cover', border: '1px solid #dddddd', cursor: 'pointer' }}
                                    onClick={() => window.open(url, '_blank')}
                                  />
                                ))}
                              </div>
                            </div>
                          )}

                          {batch.remark && (
                            <div style={{ fontSize: 13, color: '#666', lineHeight: 1.8, letterSpacing: '0.15em', marginBottom: 10 }}>
                              <p style={{ fontSize: 11, color: '#bbb', marginBottom: 4 }}>备注</p>
                              <p>{batch.remark}</p>
                            </div>
                          )}
                        </div>
                      )
                    })
                  ) : (
                    <EmptyState title="暂无批次信息" description="等待快递寄送后显示" />
                  )}
                </div>
              )}

              {(localStorage.getItem('customer_token') || localStorage.getItem('admin_token')) && activeTab === 'logistics' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {order.statusLogs && order.statusLogs.length > 0 ? (
                    order.statusLogs.map((log, index) => {
                      const statusLabels: Record<string, string> = {
                        material_pending: '待寄材料',
                        material_shipped: '材料运输',
                        received: '材料签收',
                        processing: '正在加工',
                        completed: '加工完成',
                        ready_to_ship: '待发货',
                        shipped: '已发货',
                        exception: '制作异常',
                        closed: '已关闭',
                      }
                      return (
                        <div key={log.id || index} style={{ border: '1px solid #dddddd', padding: 12 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                            <span style={{ fontSize: 13, color: '#313131', letterSpacing: '0.15em' }}>
                              {log.fromStatus
                                ? `${statusLabels[log.fromStatus] || log.fromStatus} → ${statusLabels[log.toStatus] || log.toStatus}`
                                : statusLabels[log.toStatus] || log.toStatus}
                            </span>
                            <span style={{ fontSize: 11, color: '#bbb' }}>
                              {log.createdAt ? new Date(log.createdAt).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                            </span>
                          </div>
                          {log.batchIndex !== undefined && log.batchIndex !== null && (
                            <span style={{ display: 'inline-block', fontSize: 10, color: '#fff', backgroundColor: '#313131', borderRadius: 4, padding: '2px 6px', marginBottom: 6 }}>
                              第 {log.batchIndex + 1} 批
                            </span>
                          )}
                          {log.remark && (() => {
                            const parts = log.remark.split(' | ')
                            return (
                              <div style={{ fontSize: 12, color: '#9a9a9a', marginTop: 4 }}>
                                {parts.map((part: string, idx: number) => {
                                  try {
                                    const parsed = JSON.parse(part)
                                    if (parsed.video) {
                                      return (
                                        <div key={idx}>
                                          定制面板测试：
                                          <a href={parsed.video} target="_blank" rel="noopener noreferrer" style={{ color: '#313131', textDecoration: 'underline' }}>
                                            点击查看视频
                                          </a>
                                        </div>
                                      )
                                    }
                                    if (parsed.photos?.length > 0) {
                                      return (
                                        <div key={idx}>
                                          材料照片：
                                          {parsed.photos.map((url: string, pi: number) => (
                                            <span key={pi}>
                                              <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#313131', textDecoration: 'underline' }}>
                                                图片{pi + 1}
                                              </a>
                                              {pi < parsed.photos.length - 1 ? ' ' : ''}
                                            </span>
                                          ))}
                                        </div>
                                      )
                                    }
                                    if (parsed.carrier) {
                                      return <div key={idx}>寄回快递：{parsed.carrier} {parsed.trackingNo || ''}</div>
                                    }
                                    return <div key={idx}>{part}</div>
                                  } catch {
                                    return <div key={idx}>备注：{part}</div>
                                  }
                                })}
                              </div>
                            )
                          })()}
                        </div>
                      )
                    })
                  ) : (
                    <EmptyState title="暂无记录" description="订单状态变更后将在此显示" />
                  )}
                </div>
              )}
            </div>
          </div>

          {order.subOrders && order.subOrders.length > 0 && (
            <div className="card">
              <h3 style={{ fontSize: 16, fontWeight: 500, color: '#313131', marginBottom: 16, letterSpacing: '0.15em' }}>关联订单（补做/重做）</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {order.subOrders.map((sub) => (
                  <div key={sub.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #dddddd' }}>
                    <div>
                      <span style={{ fontSize: 15, color: '#313131', letterSpacing: '0.15em' }}>{sub.orderNo}</span>
                      {sub.isFree ? (
                        <span style={{ fontSize: 11, color: '#9a9a9a', marginLeft: 8, letterSpacing: '0.15em' }}>免费补做</span>
                      ) : sub.remakeFee > 0 ? (
                        <span style={{ fontSize: 11, color: '#313131', marginLeft: 8, letterSpacing: '0.15em' }}>补费 ¥{sub.remakeFee}</span>
                      ) : null}
                    </div>
                    <StatusBadge status={sub.status} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {showShipModal && (
          <div
            className="fixed inset-0 flex items-center justify-center z-50 px-6 overflow-y-auto"
            style={{ backgroundColor: 'rgba(49, 49, 49, 0.5)' }}
            onClick={() => { setShowShipModal(false); setError(''); setShipPhotos([]); setShipSpecs([{ type: '', material: '', quantity: 1 }]); setShipCarrier(''); setCarrierDropdownOpen(false); setSpecDropdownOpen(null); setShipUseAllSpecs(true) }}
          >
            <div
              className="card w-full max-w-sm my-4"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 style={{ fontFamily: '"Noto Sans SC", sans-serif', fontSize: 18, fontWeight: 500, color: '#313131', letterSpacing: '0.15em', marginBottom: 16 }}>
                提交快递信息
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxHeight: 'calc(90vh - 160px)', overflowY: 'auto' }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, color: '#9a9a9a', marginBottom: 8, letterSpacing: '0.15em' }}>收件信息（加工后成品寄往哪里）</label>
                  <input type="text" value={shipContactName} onChange={(e) => setShipContactName(e.target.value)} placeholder="收件人姓名（必填）" className="input" style={{ marginBottom: 8 }} />
                  <input type="tel" value={shipContactPhone} onChange={(e) => setShipContactPhone(e.target.value)} placeholder="收件人电话（必填）" className="input" style={{ marginBottom: 8 }} maxLength={11} />
                  <input type="text" value={shipAddress} onChange={(e) => setShipAddress(e.target.value)} placeholder="收件地址（必填）" className="input" />
                  {isFactoryAddress(shipContactName, shipContactPhone, shipAddress) && (
                    <p style={{ marginTop: 8, padding: '8px 12px', backgroundColor: '#fff2f0', border: '1px solid #ff4d4f', borderRadius: 4, fontSize: 13, color: '#cf1322', letterSpacing: '0.15em', lineHeight: 1.6 }}>
                      ⚠️ 您填写的似乎是浅形定制工厂的收件地址，请填写<strong>您自己的收货地址</strong>（加工完成后成品将寄往此处）
                    </p>
                  )}
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, color: '#9a9a9a', marginBottom: 8, letterSpacing: '0.15em' }}>快递公司</label>
                  <div
                    onClick={() => setCarrierDropdownOpen(!carrierDropdownOpen)}
                    style={{
                      width: '100%',
                      padding: '12px 0',
                      fontSize: 15,
                      fontFamily: '"Noto Sans SC", "Inter", sans-serif',
                      color: shipCarrier ? '#313131' : '#9a9a9a',
                      border: 'none',
                      borderBottom: '1px solid #dddddd',
                      outline: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      letterSpacing: '0.15em',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span>{shipCarrier || '请选择'}</span>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9a9a9a" strokeWidth="2" style={{ transform: carrierDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </div>
                  {carrierDropdownOpen && (
                    <div style={{
                      backgroundColor: '#fff',
                      border: '1px solid #dddddd',
                      marginTop: 4,
                      maxHeight: 180,
                      overflowY: 'auto',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    }}>
                      <div
                        onClick={() => { setShipCarrier(''); setCarrierDropdownOpen(false) }}
                        style={{ padding: '10px 0', fontSize: 14, color: '#9a9a9a', cursor: 'pointer', letterSpacing: '0.15em' }}
                      >
                        请选择
                      </div>
                      {carriers.map(c => (
                        <div
                          key={c}
                          onClick={() => { setShipCarrier(c); setCarrierDropdownOpen(false) }}
                          style={{
                            padding: '10px 0',
                            fontSize: 14,
                            color: '#313131',
                            cursor: 'pointer',
                            letterSpacing: '0.15em',
                            fontWeight: shipCarrier === c ? 500 : 400,
                            borderTop: '1px solid #f0f0f0',
                          }}
                        >
                          {c}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, color: '#9a9a9a', marginBottom: 8, letterSpacing: '0.15em' }}>快递单号（材料寄出单号）</label>
                  <input type="text" value={shipTrackingNo} onChange={(e) => setShipTrackingNo(e.target.value)} placeholder="请输入快递单号" className="input" />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, color: '#9a9a9a', marginBottom: 8, letterSpacing: '0.15em' }}>定制规格</label>
                  {order && order.specs && Array.isArray(order.specs) && order.specs.length > 0 && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, cursor: 'pointer', fontSize: 13, color: '#313131', letterSpacing: '0.15em' }}>
                      <input
                        type="checkbox"
                        checked={shipUseAllSpecs}
                        onChange={(e) => {
                          const checked = e.target.checked
                          setShipUseAllSpecs(checked)
                          if (checked) {
                            // 切回一次性寄齐：恢复订单规格
                            setShipSpecs(order.specs.filter((s: any) => s.type && s.material).map((s: any) => ({ type: s.type, material: s.material, quantity: s.quantity || 1 })))
                          } else {
                            // 分批寄送：清空让客户填写
                            setShipSpecs([{ type: '', material: '', quantity: 1 }])
                          }
                        }}
                        style={{ accentColor: '#313131', width: 16, height: 16 }}
                      />
                      一次性寄齐，定制规格与订单一致
                    </label>
                  )}
                  {shipSpecs.map((spec, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'flex-start' }}>
                      <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
                        <div
                          onClick={(e) => { if (!shipUseAllSpecs) { e.stopPropagation(); setSpecDropdownOpen(specDropdownOpen === `type-${i}` ? null : `type-${i}`) } }}
                          style={{
                            width: '100%',
                            padding: '12px 0',
                            fontSize: 15,
                            fontFamily: '"Noto Sans SC", "Inter", sans-serif',
                            color: spec.type ? '#313131' : '#9a9a9a',
                            border: 'none',
                            borderBottom: '1px solid #dddddd',
                            outline: 'none',
                            background: 'none',
                            cursor: shipUseAllSpecs ? 'default' : 'pointer',
                            letterSpacing: '0.15em',
                            opacity: shipUseAllSpecs ? 0.6 : 1,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{spec.type || '类型'}</span>{!shipUseAllSpecs && (
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#9a9a9a" strokeWidth="2" style={{ flexShrink: 0, marginLeft: 4, transform: specDropdownOpen === `type-${i}` ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                            <path d="M6 9l6 6 6-6" />
                          </svg>)}
                        </div>
                        {specDropdownOpen === `type-${i}` && (
                          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, backgroundColor: '#fff', border: '1px solid #dddddd', maxHeight: 180, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                            {specTypeOptions.map(t => (
                              <div
                                key={t}
                                onClick={() => {
                                  const next = [...shipSpecs]
                                  next[i] = { ...next[i], type: t }
                                  setShipSpecs(next)
                                  setSpecDropdownOpen(null)
                                }}
                                style={{ padding: '10px 12px', fontSize: 14, color: '#313131', cursor: 'pointer', letterSpacing: '0.15em', borderTop: '1px solid #f0f0f0', fontWeight: spec.type === t ? 500 : 400 }}
                              >
                                {t}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
                        <div
                          onClick={(e) => { if (!shipUseAllSpecs) { e.stopPropagation(); setSpecDropdownOpen(specDropdownOpen === `material-${i}` ? null : `material-${i}`) } }}
                          style={{
                            width: '100%',
                            padding: '12px 0',
                            fontSize: 15,
                            fontFamily: '"Noto Sans SC", "Inter", sans-serif',
                            color: spec.material ? '#313131' : '#9a9a9a',
                            border: 'none',
                            borderBottom: '1px solid #dddddd',
                            outline: 'none',
                            background: 'none',
                            cursor: shipUseAllSpecs ? 'default' : 'pointer',
                            letterSpacing: '0.15em',
                            opacity: shipUseAllSpecs ? 0.6 : 1,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{spec.material || '材质'}</span>{!shipUseAllSpecs && (
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#9a9a9a" strokeWidth="2" style={{ flexShrink: 0, marginLeft: 4, transform: specDropdownOpen === `material-${i}` ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                            <path d="M6 9l6 6 6-6" />
                          </svg>)}
                        </div>
                        {specDropdownOpen === `material-${i}` && (
                          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, backgroundColor: '#fff', border: '1px solid #dddddd', maxHeight: 180, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                            {specMaterialOptions.map(m => (
                              <div
                                key={m}
                                onClick={() => {
                                  const next = [...shipSpecs]
                                  next[i] = { ...next[i], material: m }
                                  setShipSpecs(next)
                                  setSpecDropdownOpen(null)
                                }}
                                style={{ padding: '10px 12px', fontSize: 14, color: '#313131', cursor: 'pointer', letterSpacing: '0.15em', borderTop: '1px solid #f0f0f0', fontWeight: spec.material === m ? 500 : 400 }}
                              >
                                {m}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <input
                        type="number"
                        value={spec.quantity}
                        onChange={(e) => {
                          if (shipUseAllSpecs) return
                          const next = [...shipSpecs]
                          next[i] = { ...next[i], quantity: parseInt(e.target.value) || 1 }
                          setShipSpecs(next)
                        }}
                        className="input"
                        min={1}
                        disabled={shipUseAllSpecs}
                        style={{ width: 60, minWidth: 60, textAlign: 'center', opacity: shipUseAllSpecs ? 0.6 : 1 }}
                      />
                      {!shipUseAllSpecs && shipSpecs.length > 1 && (
                        <button
                          onClick={() => setShipSpecs(prev => prev.filter((_, idx) => idx !== i))}
                          style={{
                            width: 28, height: 28, border: '1px solid #dddddd',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#9a9a9a', background: 'none', cursor: 'pointer', flexShrink: 0,
                            fontSize: 14,
                          }}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                  {!shipUseAllSpecs && (
                  <button
                    onClick={() => setShipSpecs(prev => [...prev, { type: '', material: '', quantity: 1 }])}
                    style={{
                      fontSize: 12, color: '#313131', background: 'none', border: '1px solid #dddddd',
                      padding: '6px 12px', cursor: 'pointer', letterSpacing: '0.15em',
                    }}
                  >
                    + 添加规格
                  </button>
                  )}
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, color: '#9a9a9a', marginBottom: 8, letterSpacing: '0.15em' }}>材料照片（必填）</label>
                  <input type="file" ref={shipFileInputRef} onChange={(e) => {
                    handleShipPhotoFiles(e.target.files ?? [])
                    e.target.value = ''
                  }} accept="image/jpeg,image/png,image/webp" multiple className="hidden" />
                  <div style={{ marginBottom: 12, padding: 12, border: '1px solid #dddddd', backgroundColor: '#fafafa' }}>
                    <p style={{ fontSize: 11, color: '#9a9a9a', marginBottom: 8, letterSpacing: '0.15em' }}>参考示例</p>
                    <div style={{ display: 'flex', gap: 8, overflowX: 'auto' }}>
                      <img src={`${import.meta.env.BASE_URL}images/wood-mark.jpg`} alt="材料标记示例" onClick={() => setPreviewImage(`${import.meta.env.BASE_URL}images/wood-mark.jpg`)} style={{ width: 64, height: 64, objectFit: 'cover', border: '1px solid #dddddd', flexShrink: 0, cursor: 'pointer' }} />
                      <img src={`${import.meta.env.BASE_URL}images/stone-mark.jpg`} alt="石材标记示例" onClick={() => setPreviewImage(`${import.meta.env.BASE_URL}images/stone-mark.jpg`)} style={{ width: 64, height: 64, objectFit: 'cover', border: '1px solid #dddddd', flexShrink: 0, cursor: 'pointer' }} />
                      <img src={`${import.meta.env.BASE_URL}images/direction-mark.jpg`} alt="方位标记示例" onClick={() => setPreviewImage(`${import.meta.env.BASE_URL}images/direction-mark.jpg`)} style={{ width: 64, height: 64, objectFit: 'cover', border: '1px solid #dddddd', flexShrink: 0, cursor: 'pointer' }} />
                    </div>
                  </div>
                  <div
                    style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}
                    onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy' }}
                    onDrop={(e) => {
                      e.preventDefault()
                      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                        handleShipPhotoFiles(e.dataTransfer.files)
                      }
                    }}
                  >
                    {shipPhotos.map((photo, i) => (
                      <div key={i} style={{ position: 'relative' }}>
                        <img src={photo} alt="" style={{ width: 64, height: 64, objectFit: 'cover', border: '1px solid #dddddd' }} />
                        <button
                          onClick={() => setShipPhotos((prev) => prev.filter((_, idx) => idx !== i))}
                          style={{
                            position: 'absolute',
                            top: -4,
                            right: -4,
                            width: 16,
                            height: 16,
                            backgroundColor: '#313131',
                            color: '#fff',
                            border: 'none',
                            fontSize: 10,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            lineHeight: 1,
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => shipFileInputRef.current?.click()}
                      style={{
                        width: 64, height: 64, border: '1px dashed #dddddd',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        color: '#9a9a9a', background: 'none', cursor: 'pointer',
                      }}
                    >
                      <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                      </svg>
                      <span style={{ fontSize: 11, marginTop: 2, letterSpacing: '0.15em' }}>上传</span>
                    </button>
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, color: '#9a9a9a', marginBottom: 8, letterSpacing: '0.15em' }}>备注（选填）</label>
                  <textarea value={shipRemark} onChange={(e) => setShipRemark(e.target.value)} placeholder="如有需要提醒的信息请在此备注" className="input" rows={3} style={{ resize: 'vertical' }} />
                </div>
              </div>
              {error && <p style={{ fontSize: 13, color: '#313131', marginTop: 12, letterSpacing: '0.15em' }}>{error}</p>}
              <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
                <button onClick={() => { setShowShipModal(false); setError(''); setShipPhotos([]); setShipSpecs([{ type: '', material: '', quantity: 1 }]) }} className="flex-1 btn-secondary">取消</button>
                <button onClick={handleSelfShip} disabled={loading} className="flex-1 btn-primary">
                  {loading ? '提交中' : '确认'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showAddressModal && (
          <div
            className="fixed inset-0 flex items-center justify-center z-50 px-6 overflow-y-auto"
            style={{ backgroundColor: 'rgba(49, 49, 49, 0.5)' }}
            onClick={() => { setShowAddressModal(false); setError(''); setNewAddress(''); setNewContactName(''); setNewContactPhone('') }}
          >
            <div
              className="card w-full max-w-sm max-h-[90vh] overflow-y-auto my-4"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 style={{ fontFamily: '"Noto Sans SC", sans-serif', fontSize: 18, fontWeight: 500, color: '#313131', letterSpacing: '0.15em', marginBottom: 16 }}>
                修改收货地址
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, color: '#9a9a9a', marginBottom: 8, letterSpacing: '0.15em' }}>收货人姓名</label>
                  <input type="text" value={newContactName} onChange={(e) => setNewContactName(e.target.value)} placeholder="请输入收货人姓名" className="input" />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, color: '#9a9a9a', marginBottom: 8, letterSpacing: '0.15em' }}>联系电话</label>
                  <input type="tel" value={newContactPhone} onChange={(e) => setNewContactPhone(e.target.value)} placeholder="请输入联系电话" className="input" maxLength={11} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, color: '#9a9a9a', marginBottom: 8, letterSpacing: '0.15em' }}>详细地址</label>
                  <textarea
                    value={newAddress}
                    onChange={(e) => setNewAddress(e.target.value)}
                    placeholder="请输入详细地址（省市区+街道门牌号码）"
                    className="input"
                    rows={3}
                    style={{ resize: 'none', fontFamily: 'inherit' }}
                  />
                </div>
              </div>
              {error && <p style={{ fontSize: 13, color: '#313131', marginTop: 12, letterSpacing: '0.15em' }}>{error}</p>}
              <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
                <button onClick={() => { setShowAddressModal(false); setError(''); setNewAddress(''); setNewContactName(''); setNewContactPhone('') }} className="flex-1 btn-secondary">取消</button>
                <button onClick={handleModifyAddress} disabled={loading} className="flex-1 btn-primary">
                  {loading ? '保存中' : '保存'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {previewImage && (
        <div
          onClick={() => setPreviewImage(null)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            backgroundColor: 'rgba(0,0,0,0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img
            src={previewImage}
            alt="预览"
            style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain' }}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={() => setPreviewImage(null)}
            style={{
              position: 'absolute',
              top: 16,
              right: 16,
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ffffff',
              background: 'rgba(0,0,0,0.6)',
              border: '1px solid #666',
              borderRadius: '50%',
              fontSize: 18,
              cursor: 'pointer',
            }}
          >
            ✕
          </button>
        </div>
      )}
    </div>
  )
}
